import React, { useState, useEffect } from 'react';
import { Save, X } from 'lucide-react';
import { getPhysicalStockCount, updatePhysicalStockCount } from '../utils/physicalStockApi';
import DecimalInput from './DecimalInput';
import { blurActiveInputBeforeSave } from '../utils/numberInput';
import type { DetailedPhysicalStockCount } from '../types/physicalStockCount';

interface CountDetailsProps {
  countId: number;
  onClose: () => void;
  onCountUpdated: () => void;
}

const CountDetails: React.FC<CountDetailsProps> = ({ countId, onClose, onCountUpdated }) => {
  const [detailedCount, setDetailedCount] = useState<DetailedPhysicalStockCount | null>(null);
  const [isViewingCount, setIsViewingCount] = useState(true);
  const [isEditingCount, setIsEditingCount] = useState(false);
  const [isLoadingDetailedCount, setIsLoadingDetailedCount] = useState(true);
  const [isSavingCount, setIsSavingCount] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countItems, setCountItems] = useState<{ [key: number]: number }>({});

  useEffect(() => {
    if (countId) {
      fetchCountDetails();
    }
  }, [countId]);

  const fetchCountDetails = async () => {
    try {
      setIsLoadingDetailedCount(true);
      setError(null);
      const data = await getPhysicalStockCount(countId);
      setDetailedCount(data);
      
      // Initialize countItems with current counted quantities
      const initialCounts: { [key: number]: number } = {};
      data.items.forEach(item => {
        initialCounts[item.id] = parseFloat(item.counted_quantity);
      });
      setCountItems(initialCounts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch count details');
    } finally {
      setIsLoadingDetailedCount(false);
    }
  };

  const updateCountItem = (itemId: number, quantity: number) => {
    setCountItems(prev => ({
      ...prev,
      [itemId]: quantity
    }));
  };

  const handleEditCount = () => {
    setIsEditingCount(true);
    setIsViewingCount(false);
  };

  const cancelEdit = () => {
    setIsEditingCount(false);
    setIsViewingCount(true);
    // Reset countItems to original values
    if (detailedCount) {
      const originalCounts: { [key: number]: number } = {};
      detailedCount.items.forEach(item => {
        originalCounts[item.id] = parseFloat(item.counted_quantity);
      });
      setCountItems(originalCounts);
    }
  };

  const saveEditedCount = async () => {
    blurActiveInputBeforeSave();
    if (!detailedCount) return;

    try {
      setIsSavingCount(true);
      setError(null);

      // Prepare items for update
      const itemsToUpdate = Object.entries(countItems).map(([itemId, quantity]) => ({
        id: parseInt(itemId),
        counted_quantity: quantity
      }));

      await updatePhysicalStockCount(detailedCount.id, itemsToUpdate);
      
      // Refresh the count details
      await fetchCountDetails();
      
      // Switch back to view mode
      setIsEditingCount(false);
      setIsViewingCount(true);
      
      // Notify parent component that count was updated
      onCountUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save count');
    } finally {
      setIsSavingCount(false);
    }
  };

  if (isLoadingDetailedCount) {
    return (
      <div className="bg-white rounded-lg shadow mt-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Loading Count Details...</h3>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow mt-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Error Loading Count</h3>
        </div>
        <div className="p-6">
          <div className="text-center py-8">
            <div className="text-red-400 mb-2">
              <X className="h-12 w-12 mx-auto" />
            </div>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-150"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!detailedCount) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow mt-6">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {isViewingCount ? 'View Physical Stock Count' : 'Edit Physical Stock Count'}
            </h3>
            <p className="text-sm text-gray-500">
              Count #{detailedCount.id} - {detailedCount.warehouse.name}
            </p>
          </div>
          <div className="flex space-x-2">
            {isViewingCount && (
              <button
                onClick={handleEditCount}
                className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-800 transition-colors duration-150"
              >
                Edit
              </button>
            )}
            {isEditingCount && (
              <>
                <button
                  onClick={saveEditedCount}
                  disabled={isSavingCount}
                  className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-800 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingCount ? (
                    <>
                      <Save className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
                <button
                  onClick={cancelEdit}
                  className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-150"
                >
                  Cancel Edit
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-150"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Count Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-1">Count Date</label>
            <p className="text-sm text-gray-900">
              {new Date(detailedCount.count_date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              detailedCount.status === 'completed' ? 'bg-green-100 text-green-800' :
              detailedCount.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
              detailedCount.status === 'in_progress' ? 'bg-slate-100 text-slate-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {detailedCount.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-1">Created By</label>
            <p className="text-sm text-gray-900">{detailedCount.creator.name}</p>
          </div>
        </div>

        {/* Items Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Batch
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expected Qty
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Counted Qty
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Variance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expiry Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {detailedCount.items.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {item.product.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {item.product.code}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.batch.batch_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {parseFloat(item.expected_quantity).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isEditingCount ? (
                      <DecimalInput
                        value={countItems[item.id] || 0}
                        onCommit={(n) => updateCountItem(item.id, n)}
                        min={0}
                        className="w-24 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-800 focus:border-transparent"
                      />
                    ) : (
                      <span className="text-sm text-gray-900">
                        {parseFloat(item.counted_quantity).toFixed(2)}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`font-medium ${
                      (countItems[item.id] || parseFloat(item.counted_quantity)) - parseFloat(item.expected_quantity) === 0
                        ? 'text-green-600'
                        : (countItems[item.id] || parseFloat(item.counted_quantity)) - parseFloat(item.expected_quantity) > 0
                          ? 'text-slate-700'
                          : 'text-red-600'
                    }`}>
                      {((countItems[item.id] || parseFloat(item.counted_quantity)) - parseFloat(item.expected_quantity)).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.batch.expiry_date ? new Date(item.batch.expiry_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    }) : 'No Expiry'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CountDetails;
